import { addToCart, announce, formatMoney, setButtonState } from '@theme/ilm-cart';

/**
 * "HudHud recommends — Frequently bought together" (sections/hudhud-fbt.liquid).
 *
 * Companion books arrive as <template> rows rendered by Liquid: hand-picked from the product
 * metafield, or — when there are none — fetched from the Product Recommendations API
 * (complementary first, then related). The visitor ticks books; the total, savings and button
 * label follow, and one click adds every ticked book to the basket.
 */
class IlmHudhud extends HTMLElement {
  /** @type {AbortController | null} */
  #events = null;

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.#init();
  }

  disconnectedCallback() {
    this.#events?.abort();
  }

  async #init() {
    let source = this.querySelector('[data-hudhud-companions]');
    if (!source && this.dataset.recommendationsUrl) source = await this.#fetchCompanions();
    if (!source?.querySelector('template[data-hudhud-row-tpl]')) {
      this.remove();
      return;
    }

    const covers = this.querySelector('[data-hudhud-covers]');
    const list = this.querySelector('[data-hudhud-list]');
    for (const template of source.querySelectorAll('template[data-hudhud-cover-tpl]')) {
      covers?.append(document.importNode(/** @type {HTMLTemplateElement} */ (template).content, true));
    }
    for (const template of source.querySelectorAll('template[data-hudhud-row-tpl]')) {
      list?.append(document.importNode(/** @type {HTMLTemplateElement} */ (template).content, true));
    }
    if (source.isConnected) source.remove();

    this.#events = new AbortController();
    const { signal } = this.#events;
    this.addEventListener('change', () => this.#update(), { signal });
    this.querySelector('[data-hudhud-add]')?.addEventListener('click', () => this.#add(), { signal });
    this.hidden = false;
    this.#update();
  }

  /** @returns {Promise<Element | null>} */
  async #fetchCompanions() {
    for (const intent of ['complementary', 'related']) {
      try {
        const url = new URL(this.dataset.recommendationsUrl || '', window.location.origin);
        url.searchParams.set('intent', intent);
        const response = await fetch(url);
        if (!response.ok) continue;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const companions = doc.querySelector('[data-hudhud-companions]');
        if (companions?.querySelector('template[data-hudhud-row-tpl]')) return companions;
      } catch {
        // Try the next intent.
      }
    }
    return null;
  }

  #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-hudhud-item]')]);
  }

  #checked() {
    return this.#items().filter((item) => /** @type {HTMLInputElement | null} */ (item.querySelector('[data-hudhud-check]'))?.checked);
  }

  #update() {
    const all = this.#items();
    const checked = this.#checked();
    let total = 0;
    let compare = 0;
    for (const item of checked) {
      total += Number(item.dataset.price) || 0;
      compare += Number(item.dataset.compare) || Number(item.dataset.price) || 0;
    }
    for (const item of all) {
      const on = checked.includes(item);
      this.querySelector(`[data-hudhud-cover="${item.dataset.variantId}"]`)?.classList.toggle('is-off', !on);
    }

    const totalEl = this.querySelector('[data-hudhud-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);
    const compareEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-hudhud-compare]'));
    if (compareEl) compareEl.textContent = compare > total ? formatMoney(compare) : '';
    const saveEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-hudhud-save]'));
    if (saveEl) {
      saveEl.hidden = !(compare > total);
      saveEl.textContent = compare > total ? (this.dataset.labelSave || '').replace('[amount]', formatMoney(compare - total)) : '';
    }

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hudhud-add]'));
    if (button) {
      const count = checked.length;
      let label = this.dataset.labelAddSome || '';
      if (count === all.length) label = this.dataset.labelAddAll || label;
      if (count === 1) label = this.dataset.labelAddOne || label;
      button.textContent = label.replace('[count]', String(count));
      button.dataset.labelIdle = button.innerHTML;
      button.disabled = count === 0;
    }
  }

  async #add() {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hudhud-add]'));
    const lines = this.#checked().map((item) => ({ id: String(item.dataset.variantId), quantity: 1 }));
    if (!button || !lines.length) return;
    button.dataset.labelAdding = this.dataset.labelAdding || '';
    button.dataset.labelAdded = this.dataset.labelAdded || '';
    setButtonState(button, 'adding');
    try {
      await addToCart(lines, { source: 'ilm-fbt' });
      setButtonState(button, 'added');
      announce(this.dataset.labelAdded || '');
      setTimeout(() => setButtonState(button, 'idle'), 2400);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setButtonState(button, 'error', message);
      announce(message);
      setTimeout(() => setButtonState(button, 'idle'), 3500);
    }
  }
}

if (!customElements.get('ilm-hudhud')) customElements.define('ilm-hudhud', IlmHudhud);
